import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useNavigate, useParams } from 'react-router-dom'
import { CheckCheck, Loader2, Send } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { getMembros, type MembroComPerfil } from '@/services/equipe'
import {
  aprovarRascunho, CANAL_LABEL, CATEGORIA_MSG_LABEL, converterEmTarefa, getThread,
  listModelos, responderThread, STATUS_THREAD_LABEL, triagemThread,
  type CategoriaMensagem, type Mensagem, type MensagemModelo, type StatusThread, type ThreadComPaciente,
} from '@/services/mensagens'

export default function ThreadPage() {
  const { id } = useParams<{ id: string }>()
  const { current, user, papel } = useAuth()
  const navigate = useNavigate()
  const [thread, setThread] = useState<ThreadComPaciente | null>(null)
  const [mensagens, setMensagens] = useState<Mensagem[]>([])
  const [membros, setMembros] = useState<MembroComPerfil[]>([])
  const [modelos, setModelos] = useState<MensagemModelo[]>([])
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const [resposta, setResposta] = useState('')

  const souMedico = papel === 'medico_titular' || papel === 'medico_colaborador'

  const carregar = useCallback(async () => {
    if (!current || !id) return
    setLoading(true)
    try {
      const [r, ms, mods] = await Promise.all([
        getThread(current.workspace.id, id),
        getMembros(current.workspace.id),
        listModelos(current.workspace.id),
      ])
      if (r) {
        setThread(r.thread)
        setMensagens(r.mensagens)
      }
      setMembros(ms.filter(m => m.ativo && m.user_id))
      setModelos(mods)
    } finally {
      setLoading(false)
    }
  }, [current, id])

  useEffect(() => { carregar() }, [carregar])

  const nomeMembro = useCallback(
    (uid: string | null) => membros.find(m => m.user_id === uid)?.perfil?.nome ?? 'Equipe',
    [membros]
  )
  const medicoTitularUserId = membros.find(m => m.papel === 'medico_titular')?.user_id ?? null

  async function responder(modo: 'administrativa' | 'clinica_rascunho' | 'clinica_direta') {
    if (!current || !thread || resposta.trim().length < 2) return
    setBusy(true)
    setErro(null)
    try {
      await responderThread({
        workspaceId: current.workspace.id,
        thread,
        conteudo: resposta,
        modo,
        medicoTitularUserId,
      })
      setResposta('')
      await carregar()
    } catch {
      setErro('Não foi possível enviar.')
    } finally {
      setBusy(false)
    }
  }

  async function aprovar(m: Mensagem) {
    if (!current || !thread) return
    setBusy(true)
    setErro(null)
    try {
      await aprovarRascunho({
        workspaceId: current.workspace.id,
        thread,
        mensagem: m,
        notificarUserId: m.autor_user_id !== user?.id ? m.autor_user_id : null,
      })
      await carregar()
    } catch {
      setErro('Só médicos aprovam resposta clínica.')
    } finally {
      setBusy(false)
    }
  }

  async function converter() {
    if (!current || !thread || !user) return
    setBusy(true)
    try {
      const tid = await converterEmTarefa({
        workspaceId: current.workspace.id,
        thread,
        criadorUserId: user.id,
      })
      navigate(`/pendencias/nova?id=${tid}`)
    } finally {
      setBusy(false)
    }
  }

  async function mudar(patch: Parameters<typeof triagemThread>[0]['patch'], notificarUserId?: string | null, titulo?: string) {
    if (!current || !thread) return
    setBusy(true)
    try {
      await triagemThread({
        workspaceId: current.workspace.id,
        threadId: thread.id,
        patch,
        notificarUserId,
        tituloNotificacao: titulo,
      })
      await carregar()
    } finally {
      setBusy(false)
    }
  }

  if (!id) return null

  if (loading) {
    return (
      <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>
        <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} aria-hidden />
      </div>
    )
  }

  if (!thread) {
    return <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--muted)' }}>Conversa não encontrada.</div>
  }

  const encerrada = ['respondida', 'encerrada', 'convertida_tarefa'].includes(thread.status)

  return (
    <div>
      <header className="card" style={{ padding: 16, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 className="page-title" style={{ fontSize: 18 }}>
              <Link to={`/pacientes/${thread.paciente_id}`} style={{ color: 'var(--primary-deep)' }}>{thread.paciente_nome}</Link>
            </h1>
            <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>{thread.assunto ?? CATEGORIA_MSG_LABEL[thread.categoria]}</div>
          </div>
          <span className={`badge ${encerrada ? 'badge-ok' : 'badge-info'}`}>{STATUS_THREAD_LABEL[thread.status]}</span>
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          <select
            className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 12.5 }}
            value={thread.categoria}
            onChange={e => mudar({ categoria: e.target.value as CategoriaMensagem })}
            aria-label="Categoria" disabled={busy}
          >
            {(Object.keys(CATEGORIA_MSG_LABEL) as CategoriaMensagem[]).map(c => (
              <option key={c} value={c}>{CATEGORIA_MSG_LABEL[c]}</option>
            ))}
          </select>
          <select
            className="field-input" style={{ width: 'auto', minHeight: 34, fontSize: 12.5 }}
            value={thread.prioridade}
            onChange={e => mudar({ prioridade: e.target.value as ThreadComPaciente['prioridade'] })}
            aria-label="Prioridade" disabled={busy}
          >
            {(['baixa', 'normal', 'alta', 'urgente'] as const).map(p => <option key={p} value={p}>{p}</option>)}
          </select>
          {!encerrada && (
            <>
              <button
                className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 12.5 }} disabled={busy}
                onClick={() => mudar({ status: 'aguardando_medico' as StatusThread }, medicoTitularUserId, `Mensagem de ${thread.paciente_nome} encaminhada a você`)}
              >
                Encaminhar ao médico
              </button>
              <button className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={converter}>
                Converter em pendência
              </button>
              <button className="btn-secondary" style={{ minHeight: 34, padding: '4px 10px', fontSize: 12.5 }} disabled={busy} onClick={() => mudar({ status: 'encerrada' as StatusThread })}>
                Encerrar
              </button>
            </>
          )}
        </div>
      </header>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 14 }}>
        {mensagens.map(m => {
          const daPaciente = m.autor_tipo === 'paciente'
          return (
            <div
              key={m.id}
              className="card"
              style={{
                padding: '12px 14px',
                maxWidth: '85%',
                alignSelf: daPaciente ? 'flex-start' : 'flex-end',
                background: m.rascunho ? 'var(--warn-soft)' : daPaciente ? 'var(--card)' : 'var(--primary-soft)',
                border: m.rascunho ? '1px dashed var(--warn)' : '1px solid var(--border)',
              }}
            >
              <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 4 }}>
                {daPaciente
                  ? `${thread.paciente_nome.split(' ')[0]} · ${CANAL_LABEL[m.canal]}`
                  : nomeMembro(m.autor_user_id)}
                {' · '}
                {new Date(m.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {m.rascunho && ' · RASCUNHO — aguardando aprovação médica'}
                {m.aprovada_em && !m.rascunho && m.aprovada_por && ' · aprovada pelo médico'}
              </div>
              <div style={{ fontSize: 14.5, whiteSpace: 'pre-wrap' }}>{m.conteudo}</div>
              {m.rascunho && souMedico && (
                <button className="btn-primary" style={{ marginTop: 8, minHeight: 34, fontSize: 13 }} disabled={busy} onClick={() => aprovar(m)}>
                  <CheckCheck size={14} aria-hidden /> Aprovar e liberar
                </button>
              )}
            </div>
          )
        })}
      </div>

      {erro && <div className="field-error" role="alert" style={{ marginBottom: 10 }}>{erro}</div>}

      <div className="card" style={{ padding: 14 }}>
        {modelos.length > 0 && (
          <select
            className="field-input" style={{ minHeight: 36, fontSize: 13, marginBottom: 8 }}
            value="" onChange={e => {
              const m = modelos.find(x => x.id === e.target.value)
              if (m) setResposta(prev => (prev ? prev + '\n' : '') + m.conteudo)
            }}
            aria-label="Inserir modelo de resposta"
          >
            <option value="">Inserir modelo de resposta…</option>
            {modelos.map(m => <option key={m.id} value={m.id}>{m.titulo}</option>)}
          </select>
        )}
        <textarea
          className="field-input" rows={3} style={{ resize: 'vertical' }}
          placeholder="Escrever resposta…"
          value={resposta} onChange={e => setResposta(e.target.value)}
          aria-label="Resposta"
        />
        <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
          {souMedico ? (
            <button className="btn-primary" disabled={busy || resposta.trim().length < 2} onClick={() => responder('clinica_direta')}>
              <Send size={15} aria-hidden /> Responder
            </button>
          ) : (
            <>
              <button className="btn-primary" disabled={busy || resposta.trim().length < 2} onClick={() => responder('administrativa')}>
                <Send size={15} aria-hidden /> Responder (administrativa)
              </button>
              <button className="btn-secondary" disabled={busy || resposta.trim().length < 2} onClick={() => responder('clinica_rascunho')}>
                Preparar resposta clínica (aprovação médica)
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ marginTop: 14 }}>
        <Link to="/mensagens" style={{ fontSize: 13.5, color: 'var(--primary-deep)', fontWeight: 600 }}>← Voltar para mensagens</Link>
      </div>
    </div>
  )
}
